import { getUserCollection } from './userCollection';

// ─── Delta E (CIE76) color distance ──────────────────────────────────────────

export function hexToRgb(hex) {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r ? { r: parseInt(r[1], 16), g: parseInt(r[2], 16), b: parseInt(r[3], 16) } : null;
}

export function hexToLab(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  let r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;
  let x = (r * 0.4124564 + g * 0.3575761 + b * 0.1804375) * 100;
  let y = (r * 0.2126729 + g * 0.7151522 + b * 0.0721750) * 100;
  let z = (r * 0.0193339 + g * 0.1191920 + b * 0.9503041) * 100;
  x /= 95.047; y /= 100.0; z /= 108.883;
  x = x > 0.008856 ? Math.cbrt(x) : (7.787 * x) + 16/116;
  y = y > 0.008856 ? Math.cbrt(y) : (7.787 * y) + 16/116;
  z = z > 0.008856 ? Math.cbrt(z) : (7.787 * z) + 16/116;
  return { L: (116 * y) - 16, a: 500 * (x - y), b: 200 * (y - z) };
}

export function deltaE(hex1, hex2) {
  const lab1 = hexToLab(hex1);
  const lab2 = hexToLab(hex2);
  if (!lab1 || !lab2) return Infinity;
  return Math.sqrt(
    Math.pow(lab1.L - lab2.L, 2) +
    Math.pow(lab1.a - lab2.a, 2) +
    Math.pow(lab1.b - lab2.b, 2)
  );
}

// Find closest marker in collection for a given hex
export function findClosestInCollection(hex, collection) {
  if (!collection.length) return null;
  let best = null;
  let bestDist = Infinity;
  for (const m of collection) {
    const d = deltaE(hex, m.hex);
    if (d < bestDist) { bestDist = d; best = m; }
  }
  return best;
}

// Build a "convert & save" marker list: exact match if in collection, else closest
export function convertMarkersToCollection(markers) {
  const collection = getUserCollection();
  if (!collection.length) return [];

  const seen = new Set();
  const result = [];

  for (const m of markers) {
    // Check for exact match (same brand + code/hex)
    const exact = collection.find(c => {
      if (c.brand !== m.brand) return false;
      if (m.brand === 'Ohuhu') return c.hex?.toLowerCase() === m.hex?.toLowerCase();
      const code = (m.code || '').trim().toUpperCase();
      const cCode = (c.code || '').trim().toUpperCase();
      return code && cCode && code === cCode;
    });

    const resolved = exact || findClosestInCollection(m.hex, collection);
    if (!resolved) continue;

    // Deduplicate: use hex as identity key (good enough for display purposes)
    const key = resolved.hex?.toLowerCase();
    if (key && !seen.has(key)) {
      seen.add(key);
      result.push(resolved);
    }
  }
  return result;
}
